import React, { useState, useEffect, useCallback, memo } from "react";
import { Card, Row, Col, DatePicker, Button, Empty, Modal, Form, Select, message } from "antd";
import moment from "moment";
import {
  getGroups,
  getFacilitiesByGroup,
  getFloaterStaff,
  getPendingPeriodicals,
  getOccurrences,
  getSchedulingPeriodicals,
  setOccurrence,
  revertSetOccurrence,
  updateOccurrenceAssignment,
} from "./periodicalApi";

const { Option } = Select;
const { confirm } = Modal;

// Left-edge colours of the old app's scheduling cards (sched-item-card--*).
const PENDING_BORDER = { 1: "#fdd835", 2: "#42c7fe", 3: "#ffa726", 4: "#d50000", 5: "#8bc34a" };

// The old app's colour legend; a card's status is shown only by its left edge.
const STATUS_LABELS = { 1: "Scheduled", 2: "Assigned", 3: "In Progress", 4: "Abandoned", 5: "Completed" };

// Mirrors the old app's PeriodicalScheduling/index.js layout directly:
// Group + Facility + Year filter (no Contract step — this screen's own
// groupOnChange only ever fetches facilities, unlike Config which at least
// resolves a contract silently), then a "Pending Scheduling" panel on the
// left and the month-bucketed schedule board on the right, side by side.
// The only deliberate change is the interaction: the old screen dragged a
// card between month columns; here, clicking a pending row opens the
// "Schedule" modal (generates the year's occurrence rows via
// /set_occurrence) and clicking a scheduled card opens "Assign / Reschedule"
// (sets users + date via /update_occurrence, which the old UI only reached
// by clicking a card anyway — the drag-to-a-different-month path used a
// separate lighter /update_occurrence_data endpoint that's folded into this
// same action here instead of wired up separately).
// As in the old app's "Schedule Periodical" dialog, only the start date can be
// changed. Frequency and occurrence count come from the periodical's contract
// setup and are sent exactly as the old app sent them; the end date is always
// the end of the selected year.
const SetOccurrenceModal = ({ visible, row, year, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [frequencies, setFrequencies] = useState([]);
  const yearEnd = moment(year).endOf("year");

  useEffect(() => {
    if (!visible || !row) return;
    form.setFieldsValue({
      start_date: row.periodical_present ? moment().add(1, "month").startOf("month") : moment(),
    });
  }, [visible, row, form]);

  useEffect(() => {
    if (!visible) return;
    (async () => {
      const { error, result } = await getOccurrences();
      setFrequencies(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, [visible]);

  const frequencyName = frequencies.find((f) => f.id === row?.frequency_type_id)?.name || "-";

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const { error, result } = await setOccurrence({
        contract_facility_periodical_id: row.id,
        frequency_id: row.occurrence_type,
        occurrence_counter: row.frequency_count,
        status: "1",
        start_date: values.start_date.format("YYYY-MM-DD"),
        end_date: yearEnd.format("YYYY-MM-DD"),
      });
      if (error) message.error(result?.message || "Failed to schedule.");
      else { message.success("Periodical scheduled."); onSaved(); onClose(); }
    } finally {
      setSaving(false);
    }
  };

  const label = { marginBottom: 4, fontSize: 13, color: "#5B6472" };

  return (
    <Modal title="Schedule Periodical" visible={visible} onCancel={onClose} onOk={handleOk} okText="Save & Close" confirmLoading={saving} destroyOnClose>
      <div style={{ marginBottom: 16 }}>
        <div style={label}>Periodical Name</div>
        <div style={{ fontWeight: 600 }}>{row?.periodical_name}</div>
      </div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <div style={label}>Frequency</div>
          <div>{frequencyName}</div>
        </Col>
        <Col span={12}>
          <div style={label}>Occurrences Count In Frequency</div>
          <div>{row?.frequency_count ?? "-"}</div>
        </Col>
      </Row>
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="start_date" label="Start Date" rules={[{ required: true, message: "This field is required." }]}>
              <DatePicker
                format="DD-MM-YYYY"
                style={{ width: "100%" }}
                allowClear={false}
                disabledDate={(d) => d && d.isAfter(yearEnd, "day")}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="End Date">
              <DatePicker value={yearEnd} format="DD-MM-YYYY" style={{ width: "100%" }} disabled />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

const AssignModal = ({ visible, card, users, year, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible || !card) return;
    form.setFieldsValue({
      start_date: card.planned_start_date_time ? moment(card.planned_start_date_time) : moment(),
      user_ids: (card.user_list || []).map((u) => u.user_id),
    });
  }, [visible, card, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const dateStr = values.start_date.format("YYYY-MM-DD");
      const { error, result } = await updateOccurrenceAssignment({
        facility_periodical_occurrence_id: card.id,
        status: "1",
        planned_start_date: dateStr,
        planned_end_date: dateStr,
        user_id: (values.user_ids || []).join(","),
      });
      if (error) message.error(result?.message || "Failed to save.");
      else { message.success("Occurrence updated."); onSaved(); onClose(); }
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    confirm({
      title: "Revert schedule",
      content: `Revert this periodical's scheduled occurrences for ${moment(year).format("YYYY")}? Occurrences already in progress or completed are protected and won't be removed.`,
      okText: "Revert",
      okType: "danger",
      onOk: async () => {
        const yearStart = moment(year).startOf("year").format("YYYY-MM-DD");
        const yearEnd = moment(year).endOf("year").format("YYYY-MM-DD");
        const { error, result } = await revertSetOccurrence({
          contract_facility_periodical_id: card.contract_facility_periodical_id,
          start_date: yearStart,
          end_date: yearEnd,
          status: "0",
        });
        if (error) message.error(result?.message || "Failed to revert.");
        else { message.success("Schedule reverted."); onSaved(); onClose(); }
      },
    });
  };

  const showRevert = card && (card.status === 1 || card.status === 2);

  return (
    <Modal
      title={card ? `Assign / Reschedule — ${card.periodical_name}` : ""}
      visible={visible}
      onCancel={onClose}
      destroyOnClose
      footer={[
        showRevert && <Button key="revert" danger onClick={handleRevert}>Revert Schedule</Button>,
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="ok" type="primary" loading={saving} onClick={handleOk}>Save</Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="start_date"
          label="Date"
          rules={[{ required: true, message: "This field is required." }]}
        >
          <DatePicker
            format="DD-MM-YYYY"
            style={{ width: "100%" }}
            disabledDate={(d) => card?.planned_start_date_time && d && d.isBefore(moment(card.planned_start_date_time), "day")}
          />
        </Form.Item>
        <Form.Item name="user_ids" label="Assigned Users">
          {/* users = getFloaterStaff() rows (MyM's own /get_floater_staff_for_periodical,
              not something in this repo to confirm field names against) — falls back to
              the raw id if first_name/last_name aren't present on the response. */}
          <Select mode="multiple" showSearch optionFilterProp="children" placeholder="Select users">
            {users.map((u) => <Option key={u.user_id} value={u.user_id}>{`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.user_id}</Option>)}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const PeriodicalScheduling = () => {
  const [groups, setGroups] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [facilityId, setFacilityId] = useState(null);
  const [year, setYear] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [occurrences, setOccurrences] = useState([]); // frequency names, for the pending cards
  const [months, setMonths] = useState([]);
  const [setOccurrenceModal, setSetOccurrenceModal] = useState({ visible: false, row: null });
  const [assignModal, setAssignModal] = useState({ visible: false, card: null });

  useEffect(() => {
    (async () => {
      const [usersRes, groupsRes, occRes] = await Promise.all([getFloaterStaff(), getGroups(), getOccurrences()]);
      setOccurrences(!occRes.error && Array.isArray(occRes.result?.result) ? occRes.result.result : []);
      setUsers(!usersRes.error && Array.isArray(usersRes.result?.result) ? usersRes.result.result : []);
      setGroups(!groupsRes.error && Array.isArray(groupsRes.result?.result) ? groupsRes.result.result : []);
    })();
  }, []);

  // Group + Facility only — no Contract step — matching
  // PeriodicalScheduling/index.js's own filter directly (groupOnChange ->
  // fetchFacilityList(group_id), no contract fetch at all here, unlike
  // Config which at least resolves one silently).
  const onGroupChange = useCallback(async (value) => {
    setGroupId(value);
    setFacilityId(null);
    setFacilities([]);
    setSearched(false);
    if (!value) return;
    const { error, result } = await getFacilitiesByGroup(value);
    setFacilities(!error && Array.isArray(result?.result) ? result.result : []);
  }, []);

  const search = useCallback(async () => {
    if (!facilityId) {
      message.error("Please select a group and facility first.");
      return;
    }
    setLoading(true);
    setSearched(true);
    const [pendingRes, schedRes] = await Promise.all([
      getPendingPeriodicals(facilityId),
      getSchedulingPeriodicals(facilityId, year.format("YYYY")),
    ]);
    setPending(!pendingRes.error && Array.isArray(pendingRes.result?.result) ? pendingRes.result.result : []);
    setMonths(!schedRes.error && Array.isArray(schedRes.result?.result) ? schedRes.result.result : []);
    setLoading(false);
  }, [facilityId, year]);

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Group</div>
            <Select showSearch optionFilterProp="children" placeholder="Select a group" style={{ width: 200 }} value={groupId} onChange={onGroupChange}>
              {groups.map((g) => <Option key={g.id} value={g.id}>{g.name}</Option>)}
            </Select>
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Facility</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a facility"
              style={{ width: 220 }}
              value={facilityId}
              onChange={setFacilityId}
              disabled={!groupId}
              notFoundContent={groupId ? "No facilities for this group" : "Select a group first"}
            >
              {facilities.map((f) => <Option key={f.facility_id ?? f.id} value={f.facility_id ?? f.id}>{f.facility_name ?? f.name}</Option>)}
            </Select>
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Year</div>
            <DatePicker picker="year" value={year} onChange={(d) => d && setYear(d)} allowClear={false} />
          </div>
          <Button type="primary" loading={loading} onClick={search}>Apply</Button>
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 16, fontSize: 12, color: "#5B6472" }}>
          {Object.entries(STATUS_LABELS).map(([status, label]) => (
            <span key={status} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: PENDING_BORDER[status] }} />
              {label}
            </span>
          ))}
        </div>
      </Card>

      {searched && (
        <Row gutter={16}>
          <Col span={6}>
            {/* Pending cards: the periodical's name, its frequency and a
                status-coloured left edge. */}
            <Card
              title={<span style={{ fontSize: 13, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: "#5B6472" }}>Pending Scheduling ({pending.length})</span>}
              bodyStyle={{ padding: 12 }}
            >
              {pending.length === 0 ? (
                <div style={{ color: "#888", fontSize: 13, textAlign: "center", padding: "16px 0" }}>No pending records found.</div>
              ) : (
                pending.map((row) => (
                  <div
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSetOccurrenceModal({ visible: true, row })}
                    onKeyDown={(e) => e.key === "Enter" && setSetOccurrenceModal({ visible: true, row })}
                    style={{
                      background: "#fff",
                      border: "1px solid #e8eaee",
                      borderLeft: `4px solid ${PENDING_BORDER[row.status] || PENDING_BORDER[1]}`,
                      borderRadius: 6,
                      padding: "10px 12px",
                      marginBottom: 8,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>{row.periodical_name}</div>
                    {/* the frequency from the periodical's contract setup, e.g. "Quarterly ( x4)" */}
                    <div style={{ fontSize: 12, color: "#374151", marginTop: 2 }}>
                      {occurrences.find((f) => f.id === row.frequency_type_id)?.name || "Frequency not set"}
                    </div>
                  </div>
                ))
              )}
            </Card>
          </Col>
          <Col span={18}>
            {months.length === 0 ? (
              <Card><Empty description="No scheduling data for this year." /></Card>
            ) : (
              // Year-calendar grid — one box per month, all visible at once
              // (matches the old app's CalenderCard/DragContainer layout:
              // sched-months-grid of fixed-size month boxes, confirmed by
              // reading those files directly), instead of an accordion you
              // have to open one month at a time.
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 12 }}>
                {months.map((m) => (
                  <Card
                    key={m.month}
                    size="small"
                    title={m.month}
                    extra={<span style={{ fontSize: 12, color: "#5B6472" }}>{m.data?.length || 0}</span>}
                    bodyStyle={{ maxHeight: 360, overflowY: "auto", padding: 8 }}
                  >
                    {!m.data || m.data.length === 0 ? (
                      <div style={{ fontSize: 12, color: "#bfbfbf", textAlign: "center", padding: "12px 0" }}>Nothing scheduled</div>
                    ) : (
                      m.data.map((card) => {
                        const editable = card.status === 1 || card.status === 2;
                        return (
                          <div
                            key={card.id}
                            title={STATUS_LABELS[card.status]}
                            style={{
                              background: "#fff",
                              border: "1px solid #e8eaee",
                              borderLeft: `4px solid ${PENDING_BORDER[card.status] || "#d9d9d9"}`,
                              borderRadius: 6,
                              padding: "10px 12px",
                              marginBottom: 8,
                              cursor: editable ? "pointer" : "default",
                            }}
                            onClick={() => editable && setAssignModal({ visible: true, card })}
                          >
                            {/* as the old page: month chip, name, then "02-Oct • facility" */}
                            <span style={{ display: "inline-block", background: "#E6EFFB", color: "#1F4E8C", fontSize: 12, fontWeight: 600, borderRadius: 4, padding: "2px 8px" }}>
                              {moment(card.planned_start_date_time).format("MMM YYYY")}
                            </span>
                            <div style={{ fontWeight: 600, fontSize: 15, color: "#1a1a2e", marginTop: 6 }}>{card.periodical_name}</div>
                            <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>
                              {moment(card.planned_start_date_time).format("DD-MMM")}
                              {card.contract_facility_name ? ` • ${card.contract_facility_name}` : ""}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </Card>
                ))}
              </div>
            )}
          </Col>
        </Row>
      )}

      <SetOccurrenceModal
        visible={setOccurrenceModal.visible}
        row={setOccurrenceModal.row}
        year={year}
        onClose={() => setSetOccurrenceModal({ visible: false, row: null })}
        onSaved={search}
      />
      <AssignModal
        visible={assignModal.visible}
        card={assignModal.card}
        users={users}
        year={year}
        onClose={() => setAssignModal({ visible: false, card: null })}
        onSaved={search}
      />
    </div>
  );
};

export default memo(PeriodicalScheduling);
